import { lazy } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from '@packages/utils';
import { EIconNames } from 'components/Base/Icon';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { ReportingPage } from 'pages/private/Reporting/ReportingPage';
import {
  EventsFilled,
  EventsOutline,
  ReportingOutline,
} from 'assets/icon/icon';
import {
  useIsAdminFeature,
  useIsFeatureEnabled,
} from 'hooks/useIsFeatureEnabled';
import { EUserType } from 'data/types/user.types';
import { useAppSelector } from 'redux/store';
import { PrivateRouteType, PublicRouteType, RoutesPaths } from './Routes.types';
import useGetEventSubRoutesData from './SubRoutes/events';
import useGetWorkshopSubRoutesData from './SubRoutes/workshops';
import useGetKitsSidebarData from './SubRoutes/kitManagement';
import useGetPeopleSubRoutesData from './SubRoutes/people';
import useGetSubscribersSidebarData from './SubRoutes/subscribers';
import useGetSettingsSidebarData from './SubRoutes/settings';
import useGetFamilySubRoutesData from './SubRoutes/family';
import useGetOrganizationSubRoutesData from './SubRoutes/organization';
import useGetELCsSubRoutesData from './SubRoutes/elcs';
import useGetMyELCsSubRoutesData from './SubRoutes/myElcs';

const Login = lazy(() => import('pages/public/Login'));
const ResendPassword = lazy(() => import('pages/public/ResendPassword'));
const WorkshopCancelRegistration = lazy(
  () => import('pages/public/WorkshopCancelRegistration'),
);
const WorkshopDiscovery = lazy(() => import('pages/public/WorkshopDiscovery'));
const PageNotFound = lazy(
  () => import('../components/Shared/ErrorPageView/PageNotFound'),
);
const Profile = lazy(() => import('pages/private/Profile'));

const useGetSidebarData = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.sidebar',
  });
  const { eventSubRoutes } = useGetEventSubRoutesData();
  const { subscribersSubRoutes } = useGetSubscribersSidebarData();
  const { kitManagementSubRoutes } = useGetKitsSidebarData();
  const { settingsSubRoutes } = useGetSettingsSidebarData();
  const { familySubRoutes } = useGetFamilySubRoutesData();
  const { organizationSubRoutes } = useGetOrganizationSubRoutesData();
  const { workshopSubRoutes } = useGetWorkshopSubRoutesData();
  const { peopleSubRoutes } = useGetPeopleSubRoutesData();
  const { ELCsSubRoutes } = useGetELCsSubRoutesData();
  const { MyELCsSubRoutes } = useGetMyELCsSubRoutesData();

  const isEventsEnabled = useIsFeatureEnabled('events_management');
  const hasEventViewPermission = useCheckPermission(['EVENT_VIEW']);

  // Check if user is Lead or Admin
  const user = useAppSelector(state => state.userSlice?.user);
  const isLeadOrAdmin =
    user?.type?.some((type: EUserType) =>
      [
        EUserType.ELC_LEAD,
        EUserType.READY_ADMIN,
        EUserType.SUPER_ADMIN,
      ].includes(type),
    ) ?? false;

  const publicRoutes: PublicRouteType[] = [
    {
      component: Login,
      path: RoutesPaths.LOGIN,
    },
    {
      component: ResendPassword,
      path: RoutesPaths.RESET_PASSWORD,
    },
    {
      component: WorkshopDiscovery,
      path: RoutesPaths.WORKSHOP_DISCOVERY,
    },
    {
      component: WorkshopCancelRegistration,
      path: RoutesPaths.WORKSHOP_CANCEL_REGISTRATION,
    },
    {
      component: PageNotFound,
      path: RoutesPaths.WORKSHOP_CANCEL_REGISTRATION_NOT_FOUND,
    },
  ];

  const privateRoutes: PrivateRouteType[] = [
    {
      title: t('EVENTS'),
      component: Outlet,
      selectedIcon: <EventsFilled style={{ marginLeft: 2 }} />,
      icon: <EventsOutline style={{ marginLeft: 2 }} />,
      path: RoutesPaths.EVENTS,
      subRoutes: eventSubRoutes,
      show: isEventsEnabled && hasEventViewPermission,
      showByPermissions: isEventsEnabled && hasEventViewPermission,
      dataCy: 'events_menu',
    },
    {
      title: t('WORKSHOPS'),
      component: Outlet,
      selectedIcon: EIconNames.HANDBAG_FILLED,
      icon: EIconNames.HANDBAG,
      path: RoutesPaths.WORKSHOP,
      show: true,
      subRoutes: workshopSubRoutes,
      showByPermissions: useCheckPermission(['WORKSHOP_VIEW']),
      dataCy: 'workshops_menu',
    },
    {
      title: t('KITS_MANAGEMENT'),
      component: Outlet,
      selectedIcon: EIconNames.BOOK_FILLED,
      icon: EIconNames.BOOK,
      path: RoutesPaths.KIT_MANAGEMENT,
      show: true,
      subRoutes: kitManagementSubRoutes,
      showByPermissions: useCheckPermission(['KIT_MANAGEMENT_VIEW']),
      dataCy: 'kits_management_menu',
    },
    {
      title: t('ELCS'),
      component: Outlet,
      selectedIcon: EIconNames.CHESS_QUEEN,
      icon: EIconNames.CHESS_QUEEN_OUTLINE,
      path: RoutesPaths.ELCS,
      show: true,
      subRoutes: ELCsSubRoutes,
      showByPermissions: useCheckPermission('ELC_LIST_VIEW'),
      dataCy: 'elcs_menu',
    },
    {
      title: t('MYELCS'),
      component: Outlet,
      selectedIcon: EIconNames.CHESS_QUEEN,
      icon: EIconNames.CHESS_QUEEN_OUTLINE,
      path: RoutesPaths.MYELCS,
      show: true,
      subRoutes: MyELCsSubRoutes,
      showByPermissions: useCheckPermission('MY_ELC_LIST_VIEW'),
      dataCy: 'my_elcs_menu',
    },
    {
      title: t('ORGANIZATIONS'),
      component: Outlet,
      selectedIcon: EIconNames.PEOPLE_GROUP_FILLED,
      icon: EIconNames.PEOPLE_GROUP,
      path: RoutesPaths.ORGANIZATION,
      show: true,
      subRoutes: organizationSubRoutes,
      showByPermissions: useCheckPermission('ORGANIZATION_VIEW'),
      dataCy: 'organizations_menu',
    },
    {
      title: t('PEOPLE'),
      component: Outlet,
      selectedIcon: EIconNames.PEOPLE_PROFILE_FILLED,
      icon: EIconNames.PEOPLE_PROFILE,
      path: RoutesPaths.PEOPLE,
      show: true,
      subRoutes: peopleSubRoutes,
      showByPermissions: useCheckPermission('PEOPLE_VIEW'),
      dataCy: 'people_menu',
    },
    {
      title: t('FAMILY'),
      component: Outlet,
      selectedIcon: EIconNames.FAMILY_FILLED,
      icon: EIconNames.FAMILY,
      path: RoutesPaths.FAMILY,
      show: true,
      subRoutes: familySubRoutes,
      showByPermissions: useCheckPermission('FAMILY_VIEW'),
      dataCy: 'families_menu',
    },
    {
      title: t('SUBSCRIBERS'),
      component: Outlet,
      selectedIcon: EIconNames.DESTINY_FILLED,
      icon: EIconNames.DESTINY,
      path: RoutesPaths.SUBSCRIBERS,
      show: true,
      subRoutes: subscribersSubRoutes,
      showByPermissions: useCheckPermission([
        'SUBSCRIBERS_VIEW',
        'WAIT_LIST_VIEW',
      ]),
      dataCy: 'subscribers_menu',
    },
    {
      title: t('SETTINGS'),
      component: Outlet,
      selectedIcon: EIconNames.SETTINGS_FILLED,
      icon: EIconNames.SETTINGS,
      path: RoutesPaths.SETTINGS,
      show: isLeadOrAdmin,
      subRoutes: settingsSubRoutes,
      showByPermissions: isLeadOrAdmin,
      dataCy: 'settings_menu',
    },
    {
      title: t('REPORTING'),
      component: ReportingPage,
      selectedIcon: <ReportingOutline fill="#fff" />,
      icon: <ReportingOutline fill="#fff" />,
      path: RoutesPaths.REPORTS,
      show: useIsAdminFeature('reporting'),
      showByPermissions: useIsAdminFeature('reporting'),
    },
    {
      title: t('PROFILE'),
      component: Profile,
      selectedIcon: EIconNames.PEOPLE_PROFILE_FILLED,
      icon: EIconNames.PEOPLE_PROFILE,
      path: RoutesPaths.PROFILE,
      show: false,
      showByPermissions: true,
    },
    {
      title: t('404'),
      component: PageNotFound,
      path: RoutesPaths.PAGE_NOT_FOUND,
      show: false,
      showByPermissions: true,
    },
  ];

  const privateRoutesFilteredByPermission = privateRoutes
    .filter(item => item.showByPermissions)
    .map(el => {
      if (el.subRoutes) {
        return {
          ...el,
          subRoutes: el.subRoutes.filter(i => i.showByPermissions),
        };
      }
      return el;
    });
  return { publicRoutes, privateRoutes: privateRoutesFilteredByPermission };
};

export default useGetSidebarData;
